import { CommonModule } from '@angular/common';
import {
  Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild
} from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TooltipService } from '@shared/tooltip/tooltip.service';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';

export type ButtonTheme = 'primary' | 'secondary' | 'ghost' | 'ghostFocus' | 'greyGhost' | 'greySolid'
    | 'text' | 'primaryText' | 'blackText' | 'primaryTextLink' | 'primaryTextLinkFlushLeft'
    | 'primaryTextLinkNoPaddingNoBorder' | 'icon' | 'icon-alt' | 'icon-border'
    | 'icon-white' | 'breadcrumbText' | 'tealSolid' | 'tealGhost' | 'lightBlue' | 'darkBlue'
    | 'transparentWhite' | 'viewerControl' | 'viewerPopoverSingleCharacter' | 'viewerPopover'
    | 'viewerPopoverActive' | 'viewerPopoverSmall'
    | 'viewerPopoverSmallActive' | 'colorPalette' | 'colorPaletteActive' | 'reversePrimary'
    | 'tab' | 'tab-selected' | 'brushSizeCircle' | 'brushSizeCircleActive';

@Component({
  selector: 'zbp-button-component',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  imports: [CommonModule, ZbpIconComponent, NgbTooltip]
})
export class ButtonComponent implements OnInit {

  /** Text label for the button */
  @Input() label: string = '';

  /** Name of icon from zbpIconNames if button needs icon  */
  @Input() iconName: string = null;

  /** See {@link ZbpIconComponent#primaryColor} */
  @Input() iconPrimaryColor?: string;

  /** See {@link ZbpIconComponent#secondaryColor} */
  @Input() iconSecondaryColor?: string;

  /** Adjusts the size of the icon rendered.
   * <ul>
   * <li><strong>default</strong>: lets the icon do whatever it wants.</li>
   * <li><strong>font-size</strong>: forces icon to match font size.</li>
   * </ul>
   */
  @Input() iconSize: 'font-size' | 'default' = 'default';
  @Input() iconPosition: 'left' | 'right' | 'top' = 'left';
  /** When provided, overrides default border-radius for button theme. */
  @Input() borderRadius: string = null;
  @Input() tooltip: string | undefined;
  @Input() tooltipTriggers: string = 'hover focus';
  @Input() tooltipClass: string | undefined;
  /** Aria label to be used when extra accessibility is needed */
  @Input() ariaLabel: string = null;
  @Input() ariaLabelledBy: string = null;
  @Input() ariaControls: string = null;
  @Input() ariaCurrent: string | null = null;
  @Input() ariaExpanded: boolean | null = null;
  @Input() ariaSelected: boolean | null = null;
  /** Theme of the button which can be 'primary', 'ghost', or 'greyGhost' */
  @Input() theme: ButtonTheme = 'primary';
  /** Disabled state of the button */
  @Input() disabled: boolean = false;
  @Input() useMinWidth: boolean = false;
  /** Uses $spacing-S for top and bottom padding */
  @Input() topBottomPaddingSpacingS: boolean = false;
  /** Sets button width to 100% */
  @Input() fullWidth: boolean = false;
  /** Will add additional left and right padding to button so that label and border have more space */
  @Input() extraHorizontalPadding: boolean = false;
  /**
   * There are a couple instances of buttons that are slightly off from the common themes
   * Instead of creating a theme for each of those cases, provide a class name to add to the button
   * Any unique styling can be done in that class. You are still able to provide a theme to design off of.
   */
  @Input() customClass: string = '';
  /**
   * Choose unit to use for button font size.
   * <ul>
   * <li><strong>em</strong>: Useful for allowing iconSize='font-size' to respect the button's parent's font size.</li>
   * <li><strong>rem</strong>: Is the default, allows buttons to have a standard font size across the app.</li>
   * </ul>
   **/
  @Input() fontSizeUnit: 'em' | 'rem' = 'rem';
  @Input() fontSize: string | null = null;
  /** Unique identifier of the button */
  @Input() internalId: string;
  /** Type of the button which can be 'submit', 'button', or 'reset' */
  @Input() type: 'submit' | 'button' | 'reset' = 'button';
  /** Role of the button for when the button component is used for another purpose */
  @Input() role: string = 'button';
  /** Flag for showing a spinner instead regular content */
  @Input() showSpinner: boolean = false;
  /** Message announced by screen readers while spinner is shown */
  @Input() loadingAnnouncement: string = 'Loading';
  /**Attaches the button to the form ID provided. Can be used when the submit button lies outside the form element.
   * This enables them form to be submitted using an 'enter' keypress.
   * ________
   * Example:
   *
   * `<form id="test-form"></form>`
   *
   * `<zbp-button-component label="Save" formId="test-form"></zbp-button-component>`
   */
  @Input() formId: string = null;
  /** Number to be displayed as a prefix */
  @Input() prefixText: string | number = null;
  /** TemplateRef string support */
  @Input() tooltipTemplate: TemplateRef<any> = null;
  /** Condition to disable the tooltip */
  @Input() disableTooltip: boolean = false;
  /** Background color - initially used for the colorPalette themes */
  @Input() backgroundColor: string = null;
  /** Event emitter for click event */
  @Output() clickHandler: EventEmitter<Event> = new EventEmitter();

  /** Width of button after initial load */
  originalWidth: number;
  /** Height of button after initial load */
  originalHeight: number;

  /** Removes point events when button is disabled and there is no tooltip */
  removePointerEvents = false;
  isFocused = false;

  get liveAnnouncement(): string {
    return this.showSpinner && this.isFocused ? this.loadingAnnouncement : '';
  }

  ngOnInit(): void {
    this.removePointerEvents = this.disabled && !this.tooltip && !this.tooltipTemplate;
  }

  @ViewChild('buttonTooltip') buttonTooltip: TemplateRef<any>;

  /** HTML button element as an ElementRef*/
  @ViewChild('buttonComponent') buttonElement!: ElementRef;
  /**
   * Handles the click event and emits the event if the button is not disabled
   * @param {Event} event - The DOM event triggered on button click
   */
  handleClick(event: Event): void {
    this.tooltipService.handleTooltipOnClick();

    if (!this.showSpinner) {
      if (!this.disabled && !!this.clickHandler) {
        this.isFocused = true;
        this.clickHandler.emit(event);
      }
    } else {
      // Do not allow events when button spinner is showing
      event.preventDefault();
    }
  }

  onFocus(tooltip: NgbTooltip): void {
    this.isFocused = true;

    if (this.disableTooltip) return;

    this.tooltipService.handleTooltipVisible(tooltip);
  }

  onBlur(tooltip: NgbTooltip): void {
    this.isFocused = false;

    if (this.disableTooltip) return;

    this.tooltipService.handleTooltipHiddenImmediate(tooltip);
  }

  onMouseLeave(tooltip: NgbTooltip): void {
    if (this.disableTooltip) return;

    this.tooltipService.handleTooltipHidden(tooltip);
  }

  onMouseEnter(tooltip: NgbTooltip): void {
    if (this.disableTooltip) return;

    this.tooltipService.handleTooltipVisible(tooltip, true);
  }

  constructor(private tooltipService: TooltipService) {
  }

}
